import { Auth } from 'aws-amplify';
import { CognitoUserSession } from 'amazon-cognito-identity-js';
import store from '@/store';
import { User } from '@/model/user';
import { CognitoHostedUIIdentityProvider, CognitoUser } from '@aws-amplify/auth';
import { ICredentials } from '@aws-amplify/core';
import { COGNITO_USER_POOL_CLIENT, AUTH_API_GW_URL, TRUE_TO_STRING } from '@/constants';
import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';

export const signIn = async (email: string, password: string): Promise<any> => {
  return Auth.signIn(
    {
      username: email,
      password,
      validationData: {
        isSSOPlatformOrigin: TRUE_TO_STRING,
        clientId: store.getters.clientID
      }
    }
  );
};

export const signUp = async (user: User): Promise<any> => {
  const { email, password, name, surname, birthdate, phoneNumber, marketing, profiling } = user;
  return Auth.signUp({
    username: email,
    password,
    attributes: {
      email,
      name,
      family_name: surname,
      birthdate,
      phone_number: phoneNumber,
      'custom:profiling': profiling,
      'custom:marketing': marketing,
      'custom:client_id': store.getters.clientID,
      'custom:first_login': TRUE_TO_STRING
    },
    validationData: {
      isSSOPlatformOrigin: TRUE_TO_STRING
    },
    clientMetadata: {
      redirectURI: store.getters.redirectURI,
      language: store.getters.language
    }
  });
};

export const getAccessToken = async (): Promise<string | void> => {
  try {
    const session: CognitoUserSession = await Auth.currentSession();
    return session.getAccessToken().getJwtToken();
  } catch (error) { }
};

export const getIdToken = async (): Promise<string | void> => {
  try {
    const session: CognitoUserSession = await Auth.currentSession();
    return session.getIdToken().getJwtToken();
  } catch (error) { }
};

export const getRefreshToken = async (): Promise<string | void> => {
  try {
    const session: CognitoUserSession = await Auth.currentSession();
    return session.getRefreshToken().getToken();
  } catch (error) { }
};

export const redirectToCallbackURL = (): void => {
  const queryOrParam = (store.getters.redirectURI).includes('?') ? '&' : '?';
  const redirectURL = `${store.getters.redirectURI}${queryOrParam}` +
    `id_token=${store.getters.idToken}&` +
    `access_token=${store.getters.accessToken}&` +
    `refresh_token=${store.getters.refreshToken}`;
  window.location.href = redirectURL;
};

export const postMessageToParent = (data: any): void => {
  window.parent.postMessage(data, '*');
};

export const confirmSignUp = async (email: string, code: string): Promise<any> => {
  return Auth.confirmSignUp(email, code);
};

export const signOut = async (): Promise<any> => {
  return Auth.signOut();
};

export const forgotPassword = async (email: string): Promise<any> => {
  return Auth.forgotPassword(email, {
    redirectURI: store.getters.redirectURI,
    language: store.getters.language,
    isSSOPlatformOrigin: TRUE_TO_STRING,
    clientId: store.getters.clientID
  });
};

export const forgotPasswordSubmit = async (email: string, code: string, password: string): Promise<any> => {
  return Auth.forgotPasswordSubmit(email, code, password);
};

export const googleSignIn = async (): Promise<ICredentials> => {
  return Auth.federatedSignIn({ provider: CognitoHostedUIIdentityProvider.Google });
};

export const facebookSignIn = async (): Promise<ICredentials> => {
  return Auth.federatedSignIn({ provider: CognitoHostedUIIdentityProvider.Facebook });
};

export const appleSignIn = async (): Promise<ICredentials> => {
  return Auth.federatedSignIn({ provider: CognitoHostedUIIdentityProvider.Apple });
};

export const getCurrentUser = async (): Promise<any> => {
  try {
    return await Auth.currentAuthenticatedUser({ bypassCache: true });
  } catch (error) { }
};

export const isAuthenticated = async (): Promise<boolean> => {
  try {
    await Auth.currentAuthenticatedUser({ bypassCache: true });
    return true;
  } catch (error) {
    return false;
  }
};

export const setTokens = async (): Promise<void> => {
  const accessToken = await getAccessToken();
  const idToken = await getIdToken();
  const refreshToken = await getRefreshToken();
  if (accessToken && idToken && refreshToken) {
    store.dispatch('setAccessToken', accessToken);
    store.dispatch('setIdToken', idToken);
    store.dispatch('setRefreshToken', refreshToken);
  }
};

export const revokeTokens = async (): Promise<AxiosResponse<any>> => {
  const config: AxiosRequestConfig = {
    baseURL: AUTH_API_GW_URL,
    url: '/token/revoke',
    method: 'POST',
    data: {
      RefreshToken: store.getters.refreshToken,
      ClientID: COGNITO_USER_POOL_CLIENT
    }
  };

  return axios(config);
};

export const cleanUserFromCognito = async (): Promise<AxiosResponse<any>> => {
  const config: AxiosRequestConfig = {
    baseURL: AUTH_API_GW_URL,
    url: '/user/incomplete',
    method: 'DELETE',
    headers: {
      'accesstoken': store.getters.accessToken,
      'idtoken': store.getters.idToken
    },
    data: {
      RefreshToken: store.getters.refreshToken,
      ClientID: COGNITO_USER_POOL_CLIENT
    }
  };

  return axios(config);
};

export const isExternalProvider = (username: string): boolean => {
  return username.includes('Google') ||
    username.includes('Facebook') ||
    username.includes('SignInWithApple');
};

export const isOtpRequired =  async (): Promise<boolean> => {
  const { attributes } = await getCurrentUser();
  const annoCod = attributes['custom:annocod'];
  const progCod = attributes['custom:progcod'];

  return !annoCod || !progCod;
};

export const refreshTokensAndRedirect = async (isIframe: boolean): Promise<void> => {
  const cognitoUser: CognitoUser = await Auth.currentAuthenticatedUser();
  const currentSession = await Auth.currentSession();
  cognitoUser.refreshSession(currentSession.getRefreshToken(), async (err, session: CognitoUserSession) => {
    if (err) {
      throw new Error('Could not reload Session');
    }
    store.dispatch('setAccessToken', session.getAccessToken().getJwtToken());
    store.dispatch('setIdToken', session.getIdToken().getJwtToken());
    store.dispatch('setRefreshToken', session.getRefreshToken().getToken());
    isIframe
      ? postMessageToParent({
        idToken: session.getIdToken().getJwtToken(),
        accessToken: session.getAccessToken().getJwtToken(),
        refreshToken: session.getRefreshToken().getToken()
      })
      : redirectToCallbackURL();
  });
};